import React from 'react';
import { useCopilotChat } from '@copilotkit/react-core';
import { CopilotChat } from '@copilotkit/react-ui';
import '@copilotkit/react-ui/styles.css';

import { cva } from 'cva';

import { useModal } from 'hooks/useModal';

import { IconButton } from 'components/IconButton/IconButton';
import { MCPToolRenderer } from './MCPToolRenderer';

const drawerWrapper = cva({
  base: 'fixed z-[9999] overflow-hidden rounded-xl p-[2px] transition-all duration-300 ease-in-out',
  variants: {
    isOpen: {
      true: 'opacity-100',
      false: 'pointer-events-none opacity-0',
    },
    isExpanded: {
      true: 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
      false: 'bottom-4 left-4',
    },
  },
  defaultVariants: {
    isOpen: false,
    isExpanded: false,
  },
});

const halo = cva({
  base: 'absolute -inset-[100%] animate-halo-spin bg-[conic-gradient(from_0deg,#8b5cf6,#a855f7,#c084fc,#d8b4fe,#c084fc,#a855f7,#8b5cf6)]',
});

const drawer = cva({
  base: 'relative z-10 flex flex-col overflow-hidden rounded-[10px] bg-white transition-all duration-300 ease-in-out dark:bg-neutral-900',
  variants: {
    isExpanded: {
      true: 'h-[80vh] w-[80vw]',
      false: 'h-[min(800px,calc(100vh-8rem))] w-[400px]',
    },
  },
  defaultVariants: {
    isExpanded: false,
  },
});

type CopilotChatDrawerProps = {
  open?: boolean;
};

export function CopilotChatDrawer({ open = false }: CopilotChatDrawerProps) {
  const { close } = useModal();
  const { reset } = useCopilotChat();
  const [isExpanded, setIsExpanded] = React.useState(false);

  function handleClose() {
    close();
    setIsExpanded(false);
  }

  function handleClearChat() {
    reset();
  }

  function handleToggleExpand() {
    setIsExpanded((prev) => !prev);
  }

  return (
    <div className={drawerWrapper({ isOpen: open, isExpanded })}>
      <div className={halo()} />
      <div className={drawer({ isExpanded })}>
        <div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3 dark:border-neutral-700">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
              Ask AI Copilot
            </h2>
            <span className="rounded bg-lumeo-100 px-1.5 py-0.5 text-xs font-medium text-lumeo-700 dark:bg-lumeo-900/30 dark:text-lumeo-400">
              Preview
            </span>
          </div>
          <div className="flex items-center gap-1">
            <IconButton
              onPress={handleClearChat}
              icon="new-chat"
              size="md"
              aria-label="New chat"
            />
            <IconButton
              onPress={handleToggleExpand}
              icon={isExpanded ? 'collapse' : 'expand'}
              size="sm"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            />
            <IconButton
              onClick={handleClose}
              icon="cancel"
              size="sm"
              aria-label="Close"
            />
          </div>
        </div>
        <div className="copilot-chat-container flex-1 overflow-hidden">
          <CopilotChat
            labels={{
              title: 'Lumeo AI Copilot',
              initial:
                "Hi! I'm Lumeo's AI Copilot. I can help you setup cameras and gateways, answer questions, find/create AI workflows, or generate custom functions. What would you like to do?",
              placeholder: 'Ask me anything...',
            }}
            className="h-full"
          />
          <MCPToolRenderer />
        </div>
      </div>
    </div>
  );
}
